import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../../../contexts/LanguageContext';

const OpenServiceBulletinsDialog = ({
    visible,
    onHide,
    chassisNo,
    bulletins = [],
    isLoading = false
}) => {
    const { t } = useLanguage();

    const items = Array.isArray(bulletins) ? bulletins : [];

    const handleNavigateToBulletin = (bulletinNo) => {
        if (!bulletinNo) return;
        window.open(
            `/warranty-management/bulletins/service-bulletin-report/${bulletinNo}`,
            '_blank',
            'noopener,noreferrer'
        );
    };

    const qmnumBodyTemplate = (rowData) => {
        const bulletinNo = rowData.qmnum;
        if (!bulletinNo) return '-';

        return (
            <Tag
                value={
                    <span className="flex align-items-center gap-1">
                        <i className="pi pi-external-link" style={{ fontSize: '0.7rem' }} />
                        {bulletinNo}
                    </span>
                }
                className="cursor-pointer hover:shadow-2"
                severity="info"
                onClick={() => handleNavigateToBulletin(bulletinNo)}
                title={t('warranty.customerDeclaration.openBulletins.clickToView')}
                style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            />
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex justify-content-center p-5">
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                </div>
            );
        }

        if (items.length === 0) {
            return (
                <div className="flex justify-content-center align-items-center p-5">
                    <div className="text-center">
                        <i className="pi pi-inbox text-4xl text-400 mb-3" />
                        <p className="text-600">{t('warranty.customerDeclaration.openBulletins.empty')}</p>
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="flex align-items-center gap-2 mb-3 p-3 border-round"
                    style={{ backgroundColor: 'var(--blue-50)', border: '1px solid var(--blue-200)' }}>
                    <i className="pi pi-info-circle text-blue-500" />
                    <span className="text-blue-700 text-sm">
                        {t('warranty.customerDeclaration.openBulletins.infoBanner')}
                    </span>
                </div>

                <DataTable
                    value={items}
                    className="p-datatable-gridlines"
                    showGridlines
                    dataKey="id"
                    emptyMessage={t('warranty.customerDeclaration.openBulletins.empty')}
                    tableStyle={{ minWidth: '40rem' }}
                    size="small"
                >
                    <Column
                        field="qmnum"
                        header={
                            <span className="flex align-items-center gap-1">
                                {t('warranty.customerDeclaration.openBulletins.bulletinNo')}
                                <i
                                    className="pi pi-external-link text-xs text-500"
                                    title={t('warranty.customerDeclaration.openBulletins.clickableHint')}
                                />
                            </span>
                        }
                        style={{ width: '25%' }}
                        body={qmnumBodyTemplate}
                    />
                    <Column
                        field="description"
                        header={t('warranty.customerDeclaration.openBulletins.description')}
                        style={{ width: '75%' }}
                    />
                </DataTable>
            </>
        );
    };

    const footerContent = (
        <div className="flex justify-content-end">
            <Button
                label={t('common.close')}
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            header={
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-clipboard text-orange-500" />
                    <span>{t('warranty.customerDeclaration.openBulletins.title')}</span>
                    {chassisNo && (
                        <span className="text-600 font-normal text-sm">— {chassisNo}</span>
                    )}
                    {items.length > 0 && (
                        <Tag value={items.length} severity="warning" className="ml-2" />
                    )}
                </div>
            }
            modal
            onHide={onHide}
            style={{ width: '60vw', maxWidth: '800px' }}
            footer={footerContent}
            className="open-service-bulletins-dialog"
        >
            {renderContent()}
        </Dialog>
    );
};

export default React.memo(OpenServiceBulletinsDialog);
